import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { RefreshCw, Trash2, X } from 'lucide-react';
import { Button, Input } from '@dzseguidores/ui';
import { ApiError, api } from '@/lib/api-client';
import { formatPrice } from '@/lib/utils';

const STATUS_LABEL: Record<string, string> = {
  PENDING_PAYMENT: 'Aguardando pagamento',
  PAID: 'Pago',
  PROCESSING: 'Processando',
  PARTIAL: 'Parcial',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  REFUNDED: 'Reembolsado',
  FAILED: 'Falhou',
};

function statusClass(status: string) {
  if (status === 'COMPLETED' || status === 'PAID')
    return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (status === 'PENDING_PAYMENT' || status === 'PROCESSING' || status === 'PARTIAL')
    return 'bg-amber-500/15 text-amber-800 dark:text-amber-200';
  if (status === 'CANCELLED' || status === 'FAILED' || status === 'REFUNDED')
    return 'bg-red-500/15 text-red-700 dark:text-red-300';
  return 'bg-[var(--color-bg)] text-muted';
}

type OrderListItem = {
  id: string;
  number: string;
  status: string;
  totalCents: string;
  createdAt: string;
  paymentStatus?: string | null;
};

type OrderDetail = {
  id: string;
  number: string;
  status: string;
  email: string;
  phone: string | null;
  subtotalCents: string;
  discountCents: string;
  totalCents: string;
  createdAt: string;
  coupon: { code: string } | null;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    unitPriceCents: string;
    totalCents: string;
    targetUrl: string | null;
  }>;
  payment: {
    method: string;
    status: string;
    pixCopyPaste: string | null;
    paidAt: string | null;
  } | null;
  statusHistory: Array<{ id: string; status: string; note: string | null; createdAt: string }>;
};

export function AccountOrdersPage() {
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<OrderListItem[]>('/orders');
      setOrders(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar pedidos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openDetail(id: string) {
    setDetailLoading(true);
    setError(null);
    try {
      const data = await api.get<OrderDetail>(`/orders/${id}`);
      setDetail(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao abrir pedido.');
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Meus pedidos</h2>
          <p className="mt-1 text-sm text-muted">Acompanhe status e detalhes das suas compras.</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {error && <p className="mt-4 text-sm text-danger">{error}</p>}

      {loading ? (
        <p className="mt-6 text-sm text-muted">Carregando…</p>
      ) : orders.length === 0 ? (
        <div className="mt-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-6 text-sm text-muted">
          Nenhum pedido ainda.{' '}
          <Link to="/catalogo" className="underline hover:text-fg">
            Ver catálogo
          </Link>
        </div>
      ) : (
        <ul className="mt-6 divide-y divide-[var(--color-border)] overflow-hidden rounded-xl border border-[var(--color-border)]">
          {orders.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--color-bg)]"
                onClick={() => void openDetail(o.id)}
              >
                <div>
                  <p className="font-medium text-fg">#{o.number}</p>
                  <p className="text-xs text-muted">
                    {new Date(o.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-lg px-2 py-1 text-xs font-medium ${statusClass(o.status)}`}
                  >
                    {STATUS_LABEL[o.status] ?? o.status}
                  </span>
                  <span className="text-sm font-semibold text-fg">{formatPrice(o.totalCents)}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="flex h-full w-full max-w-lg flex-col border-l border-[var(--color-border)] bg-[var(--color-surface)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4">
              <h3 className="font-semibold text-fg">
                {detail ? `Pedido #${detail.number}` : 'Carregando…'}
              </h3>
              <button
                type="button"
                className="rounded-lg p-2 text-muted hover:bg-[var(--color-bg)] hover:text-fg"
                onClick={() => setDetail(null)}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 text-sm">
              {detailLoading && !detail ? (
                <p className="text-muted">Carregando detalhes…</p>
              ) : detail ? (
                <div className="space-y-5">
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`rounded-lg px-2 py-1 text-xs font-medium ${statusClass(detail.status)}`}
                    >
                      {STATUS_LABEL[detail.status] ?? detail.status}
                    </span>
                    <span className="text-muted">
                      {new Date(detail.createdAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">Totais</p>
                    <p className="mt-1">Subtotal: {formatPrice(detail.subtotalCents)}</p>
                    {Number(detail.discountCents) > 0 && (
                      <p>Desconto: −{formatPrice(detail.discountCents)}</p>
                    )}
                    <p className="font-semibold">Total: {formatPrice(detail.totalCents)}</p>
                    {detail.coupon && <p className="text-muted">Cupom: {detail.coupon.code}</p>}
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted">Itens</p>
                    <ul className="mt-2 space-y-2">
                      {detail.items.map((item) => (
                        <li
                          key={item.id}
                          className="rounded-xl border border-[var(--color-border)] p-3"
                        >
                          <p className="font-medium">{item.name}</p>
                          <p className="text-muted">
                            Qtd {item.quantity} · {formatPrice(item.totalCents)}
                          </p>
                          {item.targetUrl && (
                            <p className="mt-1 truncate text-xs text-muted">{item.targetUrl}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {detail.payment && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted">
                        Pagamento
                      </p>
                      <p className="mt-1">
                        {detail.payment.method} · {detail.payment.status}
                      </p>
                      {detail.payment.pixCopyPaste && detail.payment.status !== 'PAID' && (
                        <p className="mt-2 break-all rounded-lg bg-[var(--color-bg)] p-2 font-mono text-xs">
                          {detail.payment.pixCopyPaste}
                        </p>
                      )}
                    </div>
                  )}
                  {detail.statusHistory.length > 0 && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted">
                        Histórico
                      </p>
                      <ul className="mt-2 space-y-2">
                        {detail.statusHistory.map((h) => (
                          <li key={h.id} className="text-muted">
                            <span className="text-fg">{STATUS_LABEL[h.status] ?? h.status}</span>
                            {' · '}
                            {new Date(h.createdAt).toLocaleString('pt-BR')}
                            {h.note ? ` — ${h.note}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type Profile = {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  cpf: string | null;
};

export function AccountProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const data = await api.get<Profile>('/auth/me');
        setProfile(data);
        setName(data.name);
        setPhone(data.phone ?? '');
        setCpf(data.cpf ?? '');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar perfil.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await api.patch<Profile>('/auth/me', {
        name,
        phone: phone.trim() || null,
        cpf: cpf.trim() || null,
      });
      setProfile(data);
      setSuccess('Perfil atualizado.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-sm text-muted">Carregando…</p>;

  return (
    <div>
      <h2 className="text-lg font-semibold">Perfil</h2>
      <p className="mt-1 text-sm text-muted">Atualize seus dados de contato.</p>
      <form className="mt-6 max-w-md space-y-4" onSubmit={(e) => void onSubmit(e)}>
        {error && <p className="text-sm text-danger">{error}</p>}
        {success && <p className="text-sm text-emerald-600 dark:text-emerald-400">{success}</p>}
        <div>
          <label className="mb-1.5 block text-sm font-medium">E-mail</label>
          <Input value={profile?.email ?? ''} disabled />
          <p className="mt-1 text-xs text-muted">O e-mail não pode ser alterado por aqui.</p>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Nome</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">Telefone</label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="11999999999"
            inputMode="tel"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium">CPF</label>
          <Input
            value={cpf}
            onChange={(e) => setCpf(e.target.value)}
            placeholder="00000000000"
            inputMode="numeric"
          />
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar perfil'}
        </Button>
      </form>
    </div>
  );
}

type SessionRow = {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
  current: boolean;
};

export function AccountSecurityPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const data = await api.get<SessionRow[]>('/auth/sessions');
      setSessions(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPassword !== confirmPassword) {
      setError('A confirmação da nova senha não confere.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/password/change', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Senha alterada com sucesso.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao alterar senha.');
    } finally {
      setSaving(false);
    }
  }

  async function revoke(sessionId: string) {
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      await loadSessions();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao encerrar sessão.');
    }
  }

  return (
    <div className="space-y-10">
      <div>
        <h2 className="text-lg font-semibold">Segurança</h2>
        <p className="mt-1 text-sm text-muted">Altere sua senha e gerencie sessões ativas.</p>
      </div>

      <form className="max-w-md space-y-4" onSubmit={(e) => void changePassword(e)}>
        <h3 className="text-sm font-semibold text-fg">Alterar senha</h3>
        {error && <p className="text-sm text-danger">{error}</p>}
        {success && <p className="text-sm text-emerald-600 dark:text-emerald-400">{success}</p>}
        <Input
          type="password"
          placeholder="Senha atual"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        <Input
          type="password"
          placeholder="Nova senha (mín. 8)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
        />
        <Input
          type="password"
          placeholder="Confirmar nova senha"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
        />
        <Button type="submit" disabled={saving}>
          {saving ? 'Salvando…' : 'Atualizar senha'}
        </Button>
      </form>

      <div>
        <h3 className="text-sm font-semibold text-fg">Sessões ativas</h3>
        {sessions.length === 0 ? (
          <p className="mt-2 text-sm text-muted">Nenhuma sessão listada.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                <div>
                  <p className="font-medium text-fg">
                    {s.current ? 'Este dispositivo' : s.ip || 'Sessão'}
                    {s.current && (
                      <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-300">
                        atual
                      </span>
                    )}
                  </p>
                  <p className="max-w-md truncate text-xs text-muted">{s.userAgent}</p>
                  <p className="text-xs text-muted">
                    Desde {new Date(s.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                {!s.current && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void revoke(s.id)}
                  >
                    Encerrar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

type FavoriteRow = {
  id: string;
  createdAt: string;
  product: {
    id: string;
    name: string;
    slug: string;
    priceCents: string;
    promoPriceCents: string | null;
    networkName?: string;
    imageUrl?: string | null;
  };
};

export function AccountFavoritesPage() {
  const [items, setItems] = useState<FavoriteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await api.get<FavoriteRow[]>('/account/favorites'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar favoritos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(id: string) {
    try {
      await api.delete(`/account/favorites/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao remover.');
    }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold">Favoritos</h2>
      <p className="mt-1 text-sm text-muted">Produtos salvos para compra rápida.</p>
      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
      {loading ? (
        <p className="mt-6 text-sm text-muted">Carregando…</p>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-6 text-sm text-muted">
          Nenhum favorito ainda.{' '}
          <Link to="/catalogo" className="underline hover:text-fg">
            Explorar catálogo
          </Link>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {items.map((row) => {
            const price = row.product.promoPriceCents ?? row.product.priceCents;
            return (
              <li
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] p-3"
              >
                <div>
                  <Link to={`/produto/${row.product.slug}`} className="font-medium hover:underline">
                    {row.product.name}
                  </Link>
                  <p className="text-xs text-muted">
                    {row.product.networkName} · {formatPrice(price)}
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => void remove(row.id)}>
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Remover
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

type CouponCard = {
  id: string;
  code: string;
  type: string;
  value: string;
  minOrderCents: string;
  expiresAt: string | null;
};

type CouponHistory = {
  id: string;
  createdAt: string;
  coupon: CouponCard;
  order: { id: string; number: string; totalCents: string; status: string } | null;
};

function couponLabel(c: CouponCard) {
  if (c.type === 'PERCENT') return `${c.value}% off`;
  return `${formatPrice(c.value)} off`;
}

export function AccountCouponsPage() {
  const [available, setAvailable] = useState<CouponCard[]>([]);
  const [history, setHistory] = useState<CouponHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const data = await api.get<{ available: CouponCard[]; history: CouponHistory[] }>(
          '/account/coupons',
        );
        setAvailable(data.available);
        setHistory(data.history);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar cupons.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <h2 className="text-lg font-semibold">Cupons</h2>
      <p className="mt-1 text-sm text-muted">Cupons disponíveis e histórico de uso.</p>
      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
      {loading ? (
        <p className="mt-6 text-sm text-muted">Carregando…</p>
      ) : (
        <div className="mt-6 space-y-8">
          <section>
            <h3 className="text-sm font-semibold">Disponíveis</h3>
            {available.length === 0 ? (
              <p className="mt-2 text-sm text-muted">Nenhum cupom ativo no momento.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {available.map((c) => (
                  <li
                    key={c.id}
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3"
                  >
                    <p className="font-mono text-base font-semibold tracking-wide">{c.code}</p>
                    <p className="text-sm text-muted">
                      {couponLabel(c)}
                      {Number(c.minOrderCents) > 0
                        ? ` · pedido mín. ${formatPrice(c.minOrderCents)}`
                        : ''}
                      {c.expiresAt
                        ? ` · válido até ${new Date(c.expiresAt).toLocaleDateString('pt-BR')}`
                        : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section>
            <h3 className="text-sm font-semibold">Histórico de uso</h3>
            {history.length === 0 ? (
              <p className="mt-2 text-sm text-muted">Você ainda não usou cupons.</p>
            ) : (
              <ul className="mt-3 space-y-2 text-sm">
                {history.map((h) => (
                  <li
                    key={h.id}
                    className="rounded-xl border border-[var(--color-border)] px-4 py-3"
                  >
                    <p>
                      <span className="font-mono font-semibold">{h.coupon.code}</span>
                      {h.order ? ` · pedido #${h.order.number}` : ''}
                    </p>
                    <p className="text-xs text-muted">
                      {new Date(h.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

type TicketMessage = {
  id: string;
  authorRole: string;
  body: string;
  createdAt: string;
  authorName: string | null;
};

type Ticket = {
  id: string;
  subject: string;
  body: string;
  status: string;
  priority: number;
  createdAt: string;
  messages?: TicketMessage[];
};

const TICKET_STATUS: Record<string, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em andamento',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
};

export function AccountSupportPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selected = tickets.find((t) => t.id === selectedId) ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Ticket[]>('/account/support/tickets');
      setTickets(data);
      setSelectedId((prev) => (prev && data.some((t) => t.id === prev) ? prev : null));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const ticket = await api.post<Ticket>('/account/support/tickets', { subject, body });
      setTickets((prev) => [ticket, ...prev]);
      setSelectedId(ticket.id);
      setSubject('');
      setBody('');
      setSuccess('Ticket aberto. Nossa equipe responderá em breve.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao abrir ticket.');
    } finally {
      setSending(false);
    }
  }

  async function sendReply(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !reply.trim()) return;
    setSending(true);
    setError(null);
    try {
      const ticket = await api.post<Ticket>(`/account/support/tickets/${selected.id}/replies`, {
        body: reply.trim(),
      });
      setTickets((prev) => prev.map((t) => (t.id === ticket.id ? ticket : t)));
      setReply('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao enviar resposta.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold">Suporte</h2>
        <p className="mt-1 text-sm text-muted">Abra um ticket. Horário: seg–sex, 9h–18h.</p>
      </div>

      <form className="max-w-lg space-y-3" onSubmit={(e) => void onSubmit(e)}>
        {error && <p className="text-sm text-danger">{error}</p>}
        {success && <p className="text-sm text-emerald-600 dark:text-emerald-400">{success}</p>}
        <Input
          placeholder="Assunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          minLength={3}
        />
        <textarea
          className="min-h-[120px] w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-fg)] placeholder:text-[var(--color-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
          placeholder="Descreva o problema ou dúvida…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          minLength={10}
        />
        <Button type="submit" disabled={sending}>
          {sending ? 'Enviando…' : 'Abrir ticket'}
        </Button>
      </form>

      <div>
        <h3 className="text-sm font-semibold">Seus tickets</h3>
        {loading ? (
          <p className="mt-2 text-sm text-muted">Carregando…</p>
        ) : tickets.length === 0 ? (
          <p className="mt-2 text-sm text-muted">Nenhum ticket ainda.</p>
        ) : (
          <div className="mt-3 grid gap-4 lg:grid-cols-2">
            <ul className="space-y-2">
              {tickets.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition ${
                      selectedId === t.id
                        ? 'border-[var(--color-fg)] bg-[var(--color-bg)]'
                        : 'border-[var(--color-border)] hover:bg-[var(--color-bg)]'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium">{t.subject}</p>
                      <span className="text-xs text-muted">
                        {TICKET_STATUS[t.status] ?? t.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted">
                      {new Date(t.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </button>
                </li>
              ))}
            </ul>

            {selected && (
              <div className="rounded-xl border border-[var(--color-border)] p-4">
                <p className="font-semibold">{selected.subject}</p>
                <p className="text-xs text-muted">
                  {TICKET_STATUS[selected.status] ?? selected.status}
                </p>
                <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
                  {(
                    selected.messages ?? [
                      {
                        id: 'body',
                        authorRole: 'CLIENT',
                        body: selected.body,
                        createdAt: selected.createdAt,
                        authorName: null,
                      },
                    ]
                  ).map((m) => (
                    <div
                      key={m.id}
                      className={`rounded-lg px-3 py-2 text-sm ${
                        m.authorRole === 'STAFF' ? 'bg-sky-500/10' : 'bg-[var(--color-bg)]'
                      }`}
                    >
                      <p className="text-[11px] text-muted">
                        {m.authorRole === 'STAFF' ? 'Equipe DZ' : 'Você'} ·{' '}
                        {new Date(m.createdAt).toLocaleString('pt-BR')}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
                    </div>
                  ))}
                </div>
                {selected.status !== 'CLOSED' && (
                  <form className="mt-3 space-y-2" onSubmit={(e) => void sendReply(e)}>
                    <textarea
                      className="min-h-[80px] w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                      placeholder="Responder…"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      required
                    />
                    <Button type="submit" size="sm" disabled={sending || !reply.trim()}>
                      Enviar
                    </Button>
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
